import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, ArrowBackRounded } from "@mui/icons-material";
import dayjs from "../../Utils/constants/dayjsConfig";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import ComparisonTable from "./components/ComparisonTable";
import AdminPanel from "./components/AdminPanel";
import { getRaces } from "../../services/Dashboard/Table";
import { getUserInfo } from "../../services/User/Authentication";
import RaceCard, { extractRaceNumber } from "./components/Predictions/RaceCard";
import "./prediction_styles.css";
import {
  DATE_FORMAT,
  DATE_WINDOW_SIZE,
  DATE_RANGE_BEFORE,
  HOT_PICK_MIN_MARGIN,
} from "../../Utils/constants/constants";
import { userTimezone } from "../../Utils/formatDateTime";

const getInitialPredictionTab = (state, isAdmin) => {
  const tab = state?.predictionTab || state?.tab;
  if (tab === "admin" && isAdmin) return "admin";
  if (tab === "performance" && isAdmin) return "performance";
  return "predictions";
};

const PredictionsPage = () => {
  const location = useLocation();
  const [user, setUser] = useState(() => {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  });
  const [subscription, setSubscription] = useState(() => {
    const subscriptionStr = localStorage.getItem("subscription");
    return subscriptionStr ? JSON.parse(subscriptionStr) : (user?.subscription || null);
  });

  const isAdmin = user && (user.is_superuser || user.is_staff);
  const hasActiveSubscription = Boolean(
    subscription &&
    (subscription.status === "active" || subscription.status === "cancelled") &&
    !subscription.is_expired
  );
  const isRestricted = !isAdmin && !hasActiveSubscription;
  const maxRaces = !isAdmin && subscription && subscription.max_races != null && subscription.max_races !== "" ? Number(subscription.max_races) : null;
  const hasRaceLimit = maxRaces !== null && !isNaN(maxRaces) && maxRaces >= 0;
  const showHotPicks = isAdmin || (subscription && subscription.show_hot_picks !== false);

  useEffect(() => {
    const refreshUserInfo = async () => {
      try {
        const userInfo = await getUserInfo();
        const storedUser = localStorage.getItem("user");
        const currentUser = storedUser ? JSON.parse(storedUser) : null;
        
        const nextUser = {
          ...currentUser,
          ...userInfo,
        };
        
        localStorage.setItem("user", JSON.stringify(nextUser));
        setUser(nextUser);
        
        if (nextUser.subscription) {
          localStorage.setItem("subscription", JSON.stringify(nextUser.subscription));
          setSubscription(nextUser.subscription);
        } else {
          localStorage.removeItem("subscription");
          setSubscription(null);
        }
      } catch (err) {
        console.error("Failed to load user info:", err);
      }
    };
    refreshUserInfo();
  }, []);

  const navigate = useNavigate();

  const [activePredictionTab, setActivePredictionTab] = useState(() => {
    if (location.state?.activePredictionTab) return location.state.activePredictionTab;
    if (location.state?.predictionTab) return getInitialPredictionTab(location.state, isAdmin);
    return getInitialPredictionTab({}, isAdmin);
  });

  const [activeVenue, setActiveVenue] = useState(() => {
    return location.state?.predictionActiveVenue || null;
  });

  const [activeDate, setActiveDate] = useState(() => {
    const saved = location.state?.predictionActiveDate;
    const today = dayjs().format(DATE_FORMAT);
    if (!saved) return today;

    const visibleDates = Array.from({ length: DATE_WINDOW_SIZE }, (_, index) =>
      dayjs()
        .subtract(DATE_RANGE_BEFORE, "day")
        .add(index, "day")
        .format(DATE_FORMAT)
    );
    if (visibleDates.includes(saved)) {
      return saved;
    }
    return today;
  });

  const previousTabRef = useRef(activePredictionTab);

  useEffect(() => {
    if (previousTabRef.current !== activePredictionTab) {
      // Reset states when switching tabs
      setActiveVenue(null);
      setActiveDate(dayjs().format(DATE_FORMAT));
      
      // Clear location state of everything except the new tab
      navigate(location.pathname, {
        state: { activePredictionTab },
        replace: true,
      });

      previousTabRef.current = activePredictionTab;
    } else if (
      location.state?.activePredictionTab !== activePredictionTab ||
      location.state?.predictionActiveVenue !== activeVenue ||
      location.state?.predictionActiveDate !== activeDate
    ) {
      navigate(location.pathname, {
        state: {
          ...location.state,
          activePredictionTab,
          predictionActiveVenue: activeVenue,
          predictionActiveDate: activeDate,
        },
        replace: true,
      });
    }
  }, [activePredictionTab, activeVenue, activeDate, location.state, navigate, location.pathname]);

  const [loading, setLoading] = useState(true);
  const [races, setRaces] = useState([]);
  const [snapshotMinute, setSnapshotMinute] = useState("");
  const [expandedRaceId, setExpandedRaceId] = useState(null);
  const [hotPicksOpen, setHotPicksOpen] = useState(false);
  const requestIdRef = useRef(0);
  const activeTab =
    activePredictionTab === "performance" && isAdmin ? 1 : activePredictionTab === "admin" && isAdmin ? 2 : 0;

  useEffect(() => {
    if ((activePredictionTab === "admin" && !isAdmin) || (activePredictionTab === "performance" && !isAdmin)) {
      setActivePredictionTab("predictions");
    }
  }, [activePredictionTab, isAdmin]);

  const resetRaceViewState = useCallback(() => {
    setRaces([]);
    setSnapshotMinute("");
    setExpandedRaceId(null);
    setHotPicksOpen(false);
  }, []);

  const fetchRacesData = useCallback(async (date) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    resetRaceViewState();

    try {
      const filters = { date };
      const data = await getRaces(filters);
      if (requestId !== requestIdRef.current) return;

      const results = data.results || [];
      setRaces(results);
      setSnapshotMinute(data.snapshot_minute || "");

      if (results.length > 0) {
        const venuesResult = [...new Set(results.map((r) => r.venue_name))];
        setActiveVenue((prevVenue) => {
          if (prevVenue && venuesResult.includes(prevVenue)) {
            return prevVenue;
          }
          return venuesResult[0];
        });
      } else {
        setActiveVenue(null);
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error fetching races:", error);
      resetRaceViewState();
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [resetRaceViewState]);

  useEffect(() => {
    if (activeTab === 0) {
      fetchRacesData(activeDate);
    }
  }, [activeDate, activeTab, fetchRacesData]);

  const venues = useMemo(() => {
    return [...new Set(races.map((r) => r.venue_name))].filter(Boolean);
  }, [races]);

  const filteredRaces = useMemo(() => {
    if (!activeVenue) return races;
    return races.filter((r) => r.venue_name === activeVenue);
  }, [races, activeVenue]);

  const hotPicks = useMemo(() => {
    let racesToProcess = races;
    
    if (hasRaceLimit) {
      const counts = {};
      racesToProcess = races.filter((race) => {
        counts[race.venue_name] = (counts[race.venue_name] || 0) + 1;
        return counts[race.venue_name] <= maxRaces;
      });
    }

    return racesToProcess
      .map((race) => {
        const horses = race.results || [];
        if (horses.length === 0) return null;
        const first = horses.find(h => h.rank === 1);
        const runnerUp = horses.find(h => h.rank === 2);
        if (!first || !runnerUp) return null;
        const hasPredictions = horses.some((h) => Number(h.prediction_prob ?? 0) > 0);
        if (!hasPredictions) return null;
        if (first.prediction_prob - runnerUp.prediction_prob <= HOT_PICK_MIN_MARGIN) return null;
        return { race, horse: first };
      })
      .filter(Boolean);
  }, [races, hasRaceLimit, maxRaces]);

  const handleToggle = useCallback((raceId) => {
    setExpandedRaceId((prev) => (prev === raceId ? null : raceId));
  }, []);

  const visibleDates = useMemo(() => {
    return Array.from({ length: DATE_WINDOW_SIZE }, (_, index) =>
      dayjs()
        .subtract(DATE_RANGE_BEFORE, "day")
        .add(index, "day")
        .format(DATE_FORMAT)
    );
  }, []);

  const handleDateSelect = useCallback((date) => {
    if (date === activeDate) return;
    requestIdRef.current += 1;
    setLoading(true);
    resetRaceViewState();
    setActiveDate(date);
  }, [activeDate, resetRaceViewState]);

  const latestRaceCreatedAt = useMemo(() => {
    return races.reduce((latest, race) => {
      if (!race.created_at) return latest;

      const createdAt = dayjs.utc(race.created_at);
      if (!createdAt.isValid()) return latest;
      return !latest || createdAt.isAfter(latest) ? createdAt : latest;
    }, null);
  }, [races]);

  const formattedDateHeader = useMemo(() => {
    if (latestRaceCreatedAt) {
      return latestRaceCreatedAt.tz(userTimezone).format("dddd, DD MMMM YYYY");
    }

    return dayjs(activeDate).tz(userTimezone).format("dddd, DD MMMM YYYY");
  }, [activeDate, latestRaceCreatedAt]);

  const formattedSnapshotTime = useMemo(() => {
    if (latestRaceCreatedAt) {
      return latestRaceCreatedAt.tz(userTimezone).format("hh:mm A");
    }

    return snapshotMinute;
  }, [latestRaceCreatedAt, snapshotMinute]);

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)]">
      <Navbar
        activePredictionTab={activePredictionTab}
        onPredictionTabChange={setActivePredictionTab}
      />

      <div className="relative bg-[var(--color-surface)] border-b border-[var(--color-border)] overflow-hidden">
        <div className="absolute inset-0 z-[1]" />
          <div className="flex justify-center items-center px-5">
            <div className="max-w-[1280px] w-full">
              <div className="relative z-[2] xl:py-8 py-6">
                <div className="flex justify-between items-center md:mb-6 mb-4">
                  <Link to="/" className="flex items-center gap-1.5 xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-semibold">
                    <ArrowBackRounded sx={{ fontSize: 20 }} /> Back to home
                  </Link>
                </div>

                <h1 className="xl:text-4xl md:text-3xl text-2xl font-bold tracking-[-0.01em]">
                  {activeTab === 0 ? "TODAY'S " : activeTab === 1 ? "MODEL " : "ADMIN "}<span className="text-[var(--color-primary)]">{activeTab === 0 ? "PICKS" : activeTab === 1 ? "PERFORMANCE" : "PANEL"}</span>
                </h1>
                {activeTab === 0 && formattedSnapshotTime && (
                  <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] mt-2">
                    {formattedSnapshotTime && (
                      <span className="tabular">
                        Last Snapshot at {formattedDateHeader}, {formattedSnapshotTime}
                      </span>
                    )}
                  </p>
                )}
              </div>

              {activeTab === 0 && !isRestricted && (
                <>
                  {/* Date tabs */}
                  <div className="relative z-[2] mb-3">
                    <div className="date-tabs-container">
                      {visibleDates.map((d) => {
                        const active = d === activeDate;
                        const dt = dayjs(d);
                        const isToday = d === dayjs().format(DATE_FORMAT);
                        return (
                          <button
                            key={d}
                            onClick={() => handleDateSelect(d)}
                            className={`date-tab border cursor-pointer text-center ${
                              active
                                ? "active bg-[var(--color-primary)] border-[var(--color-primary)] text-black"
                                : "bg-[var(--color-bg)] border-[var(--color-border)] text-[var(--color-text)]"
                            }`}
                          >
                            <div className={`xl:text-sm md:text-xs text-[10px] font-bold uppercase tracking-[0.06em] ${active ? "opacity-[0.85]" : "opacity-70"}`}>
                              {isToday ? "Today" : dt.format("ddd")}
                            </div>
                            <div className="tabular xl:text-base md:text-sm text-xs font-bold mt-1.5 leading-[1.2]">
                              {dt.format("D MMM")}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Hot Picks Dropdown */}
                  {showHotPicks && hotPicks.length > 0 && (
                    <div className="relative z-[2] pt-3">
                      <button
                        onClick={() => setHotPicksOpen(!hotPicksOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-md)] cursor-pointer text-[var(--color-primary)]"
                      >
                        <span className="flex items-center gap-2.5 xl:text-base md:text-sm text-xs font-bold uppercase tracking-[0.1em]">
                          ★ Hot Picks
                          <p className="flex justify-center items-center bg-[var(--color-primary)] xl:text-sm md:text-xs text-[10px] text-[var(--color-bg)] w-6 h-6 rounded-full">
                            {hotPicks.length}
                          </p>
                        </span>
                        <span className={`xl:text-base md:text-sm text-xs transition-transform duration-200 ${hotPicksOpen ? "rotate-180" : "rotate-0"}`}>
                          ▾
                        </span>
                      </button>

                      {hotPicksOpen && (
                        <div className="flex gap-2 overflow-x-auto pt-3 no-scrollbar">
                          {hotPicks.map(({ race, horse }, idx) => {
                            const raceNumber = race.race_num ?? extractRaceNumber(race.race_name);

                            return (
                              <button
                                key={`${race.id || race.race_name || `${race.venue_name}-${race.race_num || idx}`}`}
                                onClick={() => {
                                  const targetId = race.id || race.race_name || `${race.venue_name}-${race.race_num || idx}`;
                                  setActiveVenue(race.venue_name);
                                  setExpandedRaceId(targetId);
                                  setHotPicksOpen(false);
                                  // Add a small delay to allow venue tab switch and rendering before scrolling
                                  setTimeout(() => {
                                    const element = document.getElementById(`race-card-${targetId}`);
                                    if (element) {
                                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                  }, 100);
                                }}
                                className={`shrink-0 min-w-[200px] md:p-4 p-3 bg-[var(--color-bg)] border rounded-[var(--radius-md)] text-left cursor-pointer ${
                                  race.venue_name === activeVenue ? "border-[rgba(200,255,0,0.25)]" : "border-[var(--color-border)]"
                                }`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="xl:text-sm md:text-xs text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--color-text-muted)] min-w-0 truncate">
                                    {race.venue_name}
                                  </div>
                                  {raceNumber != null && (
                                    <span className="tabular inline-flex items-center justify-center md:px-2.5 md:py-1 px-2 py-0.5 rounded-full border border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] xl:text-sm md:text-xs text-[10px] text-[var(--color-primary)] font-bold whitespace-nowrap">
                                      Race {raceNumber}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-2 min-w-0">
                                  {horse.horse_number != null && (
                                    <span className="tabular inline-flex items-center justify-center xl:w-[40px] xl:h-[40px] w-[30px] h-[30px] rounded-[var(--radius-md)] bg-[var(--color-primary)] xl:text-base md:text-sm text-xs text-black font-extrabold shrink-0">
                                      {horse.horse_number}
                                    </span>
                                  )}
                                  <div className="min-w-0">
                                    <div className="xl:text-base md:text-sm text-xs font-bold text-[var(--color-text)] truncate">
                                      {horse.horse_name}
                                    </div>
                                    {horse.horse_number != null && (
                                      <div className="tabular xl:text-sm md:text-xs text-[10px] text-[var(--color-text-muted)] xl:mt-0 mt-0.5">
                                        Horse {horse.horse_number}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="tabular xl:text-sm md:text-xs text-[10px] text-[var(--color-primary)] mt-1 font-bold">
                                  {Math.round(horse.prediction_prob * 100)}% confidence
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Venue tabs */}
                  {venues.length > 0 && (
                    <div className="relative z-[2] flex gap-1 overflow-x-auto pt-3 no-scrollbar">
                      {venues.map((v) => {
                        const active = v === activeVenue;
                        return (
                          <button
                            key={v}
                            onClick={() => setActiveVenue(v)}
                            className={`bg-transparent border-b-2 xl:text-base md:text-sm text-xs font-bold uppercase tracking-[0.04em] whitespace-nowrap p-4 cursor-pointer ${
                              active
                                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                                : "border-transparent text-[var(--color-text-muted)]"
                            }`}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
          </div>
        </div>
      </div>

      <div className="flex justify-center items-center xl:pb-20 md:pb-[60px] pb-10 xl:pt-6 pt-5 px-5">
        <div className="max-w-[1280px] w-full">
          {activeTab === 0 ? (
            isRestricted ? (
              <div className="flex flex-col items-center justify-center text-center md:p-8 p-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(200,255,0,0.15)] rounded-[var(--radius-md)]">
                <div className="flex items-center justify-center bg-[rgba(200,255,0,0.1)] w-16 h-16 rounded-full mb-6">
                  <Lock sx={{ fontSize: 32, color: 'var(--color-primary)' }} />
                </div>
                <h2 className="xl:text-3xl md:text-2xl text-xl font-bold text-white mb-2">
                  Active Subscription Required
                </h2>
                <p className="xl:text-lg md:text-base text-sm text-[var(--color-text-muted)] xl:max-w-md md:max-w-sm mb-8">
                  You currently do not have an active subscription plan. Subscribe to a plan to unlock AI race predictions and top picks.
                </p>
                <Link
                  to="/"
                  className="w-fit xl:text-base md:text-sm text-xs bg-[var(--color-primary)] text-black border-none py-3 px-6 rounded-[var(--radius-md)] font-extrabold uppercase cursor-pointer tracking-[0.06em]"
                >
                  View Subscription Plans
                </Link>
              </div>
            ) : loading ? (
              <div className="grid gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton xl:h-20 h-16 bg-[var(--color-surface)] rounded-[var(--radius-md)] opacity-50" />
                ))}
              </div>
            ) : filteredRaces.length === 0 ? (
              <div className="xl:text-xl md:text-lg text-base xl:pt-14 md:pt-10 pt-5 text-center text-[var(--color-text-muted)]">
                No races available on {dayjs(activeDate).tz(userTimezone).format("MMM DD")}.
              </div>
            ) : (
              <>
                {(hasRaceLimit ? filteredRaces.slice(0, maxRaces) : filteredRaces).map((race, idx) => {
                  const targetId = race.id || race.race_name || `${race.venue_name}-${race.race_num || idx}`;
                  return (
                    <RaceCard
                      key={targetId}
                      race={race}
                      raceId={targetId}
                      expanded={expandedRaceId === targetId}
                      onToggle={handleToggle}
                      showHotPicks={showHotPicks}
                    />
                  );
                })}
                {hasRaceLimit && filteredRaces.length > maxRaces && (
                  <div className="flex flex-col items-center justify-center text-center md:p-8 p-4 gap-2 bg-[var(--color-surface)] border border-[rgba(200,255,0,0.15)] rounded-[var(--radius-md)]">
                    <Lock sx={{ fontSize: 30, color: 'var(--color-primary)' }} />
                    <span className="xl:text-xl md:text-lg text-base font-bold text-white">
                      Unlock {filteredRaces.length - maxRaces} More Race{filteredRaces.length - maxRaces !== 1 ? 's' : ''}
                    </span>
                    <p className="xl:text-lg md:text-base text-sm text-[var(--color-text-muted)] mb-6">
                      Your active plan is limited to {maxRaces} race{maxRaces !== 1 ? 's' : ''} per day. Upgrade your plan to see all race predictions, advanced stats, and daily hot picks.
                    </p>
                    <Link
                      to="/"
                      className="w-fit xl:text-base md:text-sm text-xs bg-[var(--color-primary)] text-black border-none py-3 px-6 rounded-[var(--radius-md)] font-extrabold uppercase cursor-pointer tracking-[0.06em]"
                    >
                      Update your plan
                    </Link>
                  </div>
                )}
              </>
            )
          ) : activeTab === 1 ? (
            <ComparisonTable />
          ) : (
            isAdmin && <AdminPanel />
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PredictionsPage;
